import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ListTodo, Loader2, SearchX, Users } from "lucide-react";

import SearchField from "@/components/common/SearchField";
import UserAvatar from "@/components/common/UserAvatar";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { MIN_SEARCH_LENGTH, useGetSearchResults } from "@/hooks/useSearch";
import { cn } from "@/utils/common.utils";
import { buildClientDealPath, buildCollabDealPath, buildDealDetailPath } from "@/routes/url";
import type { SearchDealView, SearchResponse } from "@/types/search.types";

/** One flat, keyboard-navigable row. The groups are only a visual heading —
 *  arrow keys move through every result in order regardless of group. */
interface Row {
  key: string;
  group: "deals" | "tasks" | "people";
  label: string;
  sublabel: string;
  path: string | null;
  person?: string;
  photo?: string;
}

const GROUP_META = {
  deals: { title: "Deals", icon: Building2 },
  tasks: { title: "Tasks", icon: ListTodo },
  people: { title: "People", icon: Users },
} as const;

/** The three role-shaped deal pages. `view` is decided server-side. */
function dealPath(dealId: string, view: SearchDealView | null): string | null {
  if (!dealId) return null;
  if (view === "client") return buildClientDealPath(dealId);
  if (view === "collab") return buildCollabDealPath(dealId);
  return buildDealDetailPath(dealId);
}

function toRows(data?: SearchResponse): Row[] {
  if (!data) return [];
  const { deals, tasks, people } = data.groups;
  return [
    ...deals.map((deal) => ({
      key: `deal:${deal.id}`,
      group: "deals" as const,
      label: deal.address || "Untitled deal",
      sublabel: [deal.client_name, deal.phase_label].filter(Boolean).join(" · "),
      path: dealPath(deal.id, deal.view),
    })),
    ...tasks.map((task) => ({
      key: `task:${task.id}`,
      group: "tasks" as const,
      label: task.title,
      sublabel: [task.deal_address, task.phase_label].filter(Boolean).join(" · "),
      path: dealPath(task.deal_id, task.view),
    })),
    ...people.map((person) => ({
      key: `person:${person.kind}:${person.id}`,
      group: "people" as const,
      label: person.name,
      sublabel: [
        person.role ? person.role.replace(/_/g, " ") : "",
        person.kind === "deal_contact" ? person.deal_address : person.email,
      ].filter(Boolean).join(" · "),
      // A teammate belongs to the org rather than to one deal, so there is
      // nothing to open — the row still answers "who is this?".
      path: person.deal_id ? dealPath(person.deal_id, person.view) : null,
      person: person.name,
      photo: person.photo_url,
    })),
  ];
}

export default function GlobalSearch({ className }: { className?: string }) {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Debounced so typing an address is one request at the end, not one per
  // character — and so responses can't land out of order on a slow connection.
  const debounced = useDebouncedValue(query.trim(), 250);
  const longEnough = debounced.length >= MIN_SEARCH_LENGTH;
  const { data, isFetching } = useGetSearchResults({ q: debounced, limit: 5 }, { enabled: longEnough });

  const rows = React.useMemo(() => (longEnough ? toRows(data) : []), [data, longEnough]);

  // A new result set invalidates the old highlight position.
  React.useEffect(() => setActive(0), [debounced]);

  // Click outside closes. Escape is handled on the input, which is where focus
  // is while the dropdown is open.
  React.useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  // ⌘K / Ctrl+K focuses the box from anywhere, which is the shortcut the
  // placeholder's sibling hints already advertise elsewhere in the app.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // `key` is optional in practice: password managers and IME composition
      // dispatch keydown events without it, and reading `.toLowerCase()` off
      // undefined throws out of a document-level listener — which surfaces as
      // an uncaught runtime error on pages that have nothing to do with search.
      if (typeof event.key !== "string") return;
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const select = (row?: Row) => {
    if (!row?.path) return;
    setOpen(false);
    setQuery("");
    navigate(row.path);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!rows.length) return;
      event.preventDefault();
      setOpen(true);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => (current + step + rows.length) % rows.length);
      return;
    }
    if (event.key === "Enter") {
      // Only intercept Enter when there is something to open, so the key still
      // does nothing surprising on an empty or still-loading box.
      if (rows[active]?.path) {
        event.preventDefault();
        select(rows[active]);
      }
    }
  };

  const showPanel = open && longEnough;
  let rowIndex = -1;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <SearchField
        ref={inputRef}
        value={query}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
        onClear={() => { setQuery(""); setOpen(false); inputRef.current?.focus(); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onInputKeyDown}
        placeholder="Search deals, clients, agents, addresses…"
        inputClassName="h-[41px] truncate border-transparent bg-dash-searchBg text-dash-ink placeholder:text-dash-placeholder placeholder:truncate focus-visible:ring-1 focus-visible:ring-dash-greenDeep"
        testId="dashboard-header-search"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls="global-search-results"
        aria-autocomplete="list"
      />

      {showPanel && (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-50 max-h-[70vh] overflow-y-auto rounded-xl border border-dash-border bg-white py-1.5 shadow-lg"
          data-testid="global-search-results"
        >
          {rows.length === 0 ? (
            <div className="flex items-center gap-2.5 px-4 py-6 text-sm text-dash-muted" data-testid="global-search-empty">
              {isFetching ? (
                <><Loader2 className="size-4 animate-spin" /> Searching…</>
              ) : (
                <><SearchX className="size-4" /> No matches for “{debounced}”</>
              )}
            </div>
          ) : (
            (["deals", "tasks", "people"] as const).map((group) => {
              const groupRows = rows.filter((row) => row.group === group);
              if (!groupRows.length) return null;
              const { title, icon: Icon } = GROUP_META[group];
              return (
                <div key={group} className="py-1">
                  <p className="px-4 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-dash-faint">
                    {title}
                  </p>
                  {groupRows.map((row) => {
                    rowIndex += 1;
                    const index = rowIndex;
                    return (
                      <button
                        key={row.key}
                        type="button"
                        role="option"
                        aria-selected={index === active}
                        // Pointer selection has to survive the input keeping
                        // focus, so highlight on hover and act on mouse down.
                        onMouseEnter={() => setActive(index)}
                        onMouseDown={(event) => { event.preventDefault(); select(row); }}
                        className={cn(
                          "flex w-full items-center gap-3 px-4 py-2 text-left transition-colors",
                          index === active ? "bg-dash-hover" : "hover:bg-dash-hover",
                          !row.path && "cursor-default",
                        )}
                        data-testid={`global-search-row-${group}`}
                      >
                        {row.person ? (
                          <UserAvatar name={row.person} src={row.photo} colored className="size-7 shrink-0" />
                        ) : (
                          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-dash-searchBg text-dash-greenDeep">
                            <Icon className="size-3.5" />
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-dash-ink">{row.label}</span>
                          {row.sublabel && (
                            <span className="block truncate text-xs capitalize text-dash-faint">{row.sublabel}</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
